/**
 * transcribe-client.ts — Browser-side Transcribe streaming client.
 *
 * Path choice: raw WebSocket to the server-generated SigV4 presigned URL.
 * Rationale: the server returns a pre-signed WSS URL (no AWS credentials needed
 * in the browser). The @aws-sdk/client-transcribe-streaming SDK requires credentials
 * at construction time, making it unsuitable for this presigned-URL approach.
 * The spike validated HTTP 101 with the manual SigV4 presigning pattern (Option B).
 *
 * Protocol:
 * - Browser opens WebSocket to the presigned WSS URL
 * - Each audio chunk is wrapped in AWS event-stream AudioEvent framing and sent
 * - Transcribe returns binary event-stream frames containing transcript JSON
 * - Silence heartbeat: every 10s a 100ms silence frame (Int16Array(1600) of zeros)
 *   is sent to prevent the 15s Transcribe silence timeout (Pitfall A, VOICE-02)
 *
 * Security:
 * - T-02-36: presigned URL is never logged; only the hostname is logged on error
 * - T-02-31: getUserMedia permission handled by caller (useTranscribe hook)
 * - T-02-37: stop() disconnects AudioContext and stops media tracks
 *
 * iOS note (Pitfall C): AudioContext must be created inside a user-gesture handler
 * (or resumed after one). The caller (useTranscribe hook) must ensure this.
 */

import { wrapPcmInAudioEvent, parseTranscriptEvent } from './audio-encoding.js';

export interface TranscribeClientOptions {
  /** SigV4 presigned WSS URL from GET /transcribe-url */
  presignedUrl: string;
}

/**
 * Browser TranscribeClient wrapping a raw WebSocket to the Transcribe streaming endpoint.
 *
 * Usage:
 *   const client = new TranscribeClient({ presignedUrl: url });
 *   await client.start(mediaStream, onPartial, onFinal);
 *   // ...later...
 *   await client.stop();
 */
export class TranscribeClient {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private readonly presignedUrl: string;

  constructor(opts: TranscribeClientOptions) {
    this.presignedUrl = opts.presignedUrl;
  }

  /**
   * Start streaming audio from the given MediaStream to Transcribe.
   *
   * @param audioStream  MediaStream from getUserMedia (must be 16kHz or resampled)
   * @param onPartial    Called with partial transcript text as it arrives
   * @param onFinal      Called with final (non-partial) transcript text
   */
  async start(
    audioStream: MediaStream,
    onPartial: (text: string) => void,
    onFinal: (text: string) => void,
  ): Promise<void> {
    // Create AudioContext at 16kHz to match Transcribe sample rate
    // Note: AudioContext({ sampleRate: 16000 }) may resample on some browsers;
    // the recording-processor.js worklet will see 16kHz frames from AudioContext.
    this.audioContext = new AudioContext({ sampleRate: 16000 });

    // Load the AudioWorklet processor (served as static file from /public/worklets/)
    await this.audioContext.audioWorklet.addModule('/worklets/recording-processor.js');

    // Connect mic → worklet (NOT to destination — we don't want mic feedback)
    this.sourceNode = this.audioContext.createMediaStreamSource(audioStream);
    this.workletNode = new AudioWorkletNode(this.audioContext, 'recording-processor');
    this.sourceNode.connect(this.workletNode);
    // Intentionally NOT connecting workletNode to audioContext.destination

    // Open WebSocket to the presigned Transcribe endpoint
    const ws = new WebSocket(this.presignedUrl);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    // Wait for WebSocket to open before wiring the audio message handler
    await new Promise<void>((resolve, reject) => {
      const onOpen = () => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        resolve();
      };
      const onError = (e: Event) => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        // T-02-36: only log hostname, not the full presigned URL (strips credentials)
        const safeHost = new URL(this.presignedUrl).hostname;
        console.error(`TranscribeClient: WebSocket error connecting to ${safeHost}`, e);
        reject(new Error(`Transcribe WebSocket failed to open (host: ${safeHost})`));
      };
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
    });

    // Handle incoming binary frames from Transcribe (transcript results)
    ws.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      const result = parseTranscriptEvent(event.data);
      if (!result) return;
      if (result.isPartial) {
        onPartial(result.transcript);
      } else {
        onFinal(result.transcript);
      }
    };

    // Handle unexpected close — surface to caller for reconnect logic
    ws.onclose = (event: CloseEvent) => {
      if (event.code !== 1000) {
        const safeHost = new URL(this.presignedUrl).hostname;
        console.warn(
          `TranscribeClient: WebSocket closed unexpectedly (code=${event.code}, host=${safeHost})`,
        );
      }
    };

    // Wire AudioWorklet → WebSocket: accumulate 200ms of audio before sending.
    // The worklet fires at ~125Hz (128 frames @ 16kHz = 8ms/chunk). Sending each
    // tiny chunk individually causes ~125 CRC32 computations + WebSocket sends per
    // second, which creates backpressure and lag. Batching to 3200 samples (200ms)
    // reduces sends to ~5/sec with no effect on transcription accuracy.
    const TARGET_SAMPLES = 3200; // 200ms at 16kHz
    let pendingBuffers: ArrayBuffer[] = [];
    let pendingSamples = 0;

    this.workletNode.port.onmessage = (e: MessageEvent<{ type: string; buffer: ArrayBuffer }>) => {
      if (e.data.type !== 'audio') return;
      if (ws.readyState !== WebSocket.OPEN) return;

      pendingBuffers.push(e.data.buffer);
      pendingSamples += e.data.buffer.byteLength >> 1; // Int16: 2 bytes/sample

      if (pendingSamples >= TARGET_SAMPLES) {
        const combined = new Int16Array(pendingSamples);
        let offset = 0;
        for (const buf of pendingBuffers) {
          const chunk = new Int16Array(buf);
          combined.set(chunk, offset);
          offset += chunk.length;
        }
        ws.send(wrapPcmInAudioEvent(combined.buffer));
        pendingBuffers = [];
        pendingSamples = 0;
      }
    };

    // Silence heartbeat: send 100ms of silence (1600 Int16 samples at 16kHz) every 10s
    // Prevents the 15s Transcribe silence timeout (Pitfall A, VOICE-02)
    this.heartbeatInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        const silence = new Int16Array(1600); // 100ms @ 16kHz — all zeros
        ws.send(wrapPcmInAudioEvent(silence.buffer));
      }
    }, 10_000);
  }

  /**
   * Stop streaming: close the WebSocket, disconnect AudioContext, clear heartbeat.
   * Safe to call multiple times.
   */
  async stop(): Promise<void> {
    // Clear silence heartbeat
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    // Disconnect audio graph
    try {
      this.sourceNode?.disconnect();
      this.workletNode?.disconnect();
    } catch {
      // ignore disconnect errors
    }
    this.sourceNode = null;
    this.workletNode = null;

    // Close AudioContext
    if (this.audioContext && this.audioContext.state !== 'closed') {
      try {
        await this.audioContext.close();
      } catch {
        // ignore
      }
    }
    this.audioContext = null;

    // Close WebSocket cleanly
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close(1000, 'stopped');
    }
    this.ws = null;
  }
}
